import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { DecisionKindEnum } from "@/enum/decision-kind";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { formatDateToDMY } from "@/utils/format-date-ddmmyyyy";
import { formatToBRL } from "@/utils/formatToMoney";
import { AlertTriangle, Check, X } from "lucide-react";
import { useState } from "react";

import {
  DECISION_REASON_MIN_LENGTH,
  FIRST_INSTALLMENT_INDEX,
  MAX_COMMENT_LENGTH,
} from "../constants";
import { PAYROLL_VOUCHER_LABELS } from "../messages";
import { PayrollVoucher, VoucherDecision } from "../types";
import {
  buildVoucherPreflight,
  skipLabelOf,
  VoucherBatchPreflight,
  VoucherSkip,
} from "../utils/batch-preflight";
import { installmentAmountsOf, installmentsOf } from "../utils/decision-consequence";

export const BATCH_PREFLIGHT_TEST_ID: string = "voucher-batch-preflight";

export const BATCH_SKIPPED_TEST_ID: string = "voucher-batch-skipped";

export const BATCH_EFFECT_TEST_ID: string = "voucher-batch-effect";

export const BATCH_REASON_TEST_ID: string = "voucher-batch-reason";

const BATCH_REASON_FIELD_ID: string = "voucher-batch-reason-field";

interface BatchDecisionModalProps {
  selected: readonly PayrollVoucher[];
  kind: DecisionKindEnum;
  onConfirm: (
    vouchers: readonly PayrollVoucher[],
    decision: VoucherDecision,
  ) => Promise<void>;
}

export function BatchDecisionModal({
  selected,
  kind,
  onConfirm,
}: BatchDecisionModalProps): JSX.Element {
  const { handleModal } = useDialogModal();
  const [reason, setReason] = useState<string>("");
  const [showReasonError, setShowReasonError] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const isApproval: boolean = kind === DecisionKindEnum.APPROVE;

  const actionLabel: string = isApproval
    ? PAYROLL_VOUCHER_LABELS.APPROVE
    : PAYROLL_VOUCHER_LABELS.REJECT;

  const preflight: VoucherBatchPreflight = buildVoucherPreflight(
    selected,
    kind,
  );

  const count: number = preflight.valid.length;

  const trimmedReason: string = reason.trim();
  const reasonIsValid: boolean =
    isApproval || trimmedReason.length >= DECISION_REASON_MIN_LENGTH;

  const handleCancel = (): void => {
    handleModal({ isOpen: false });
  };

  const handleConfirm = async (): Promise<void> => {
    if (isSubmitting || count === 0) return;

    if (!reasonIsValid) {
      setShowReasonError(true);
      return;
    }

    setIsSubmitting(true);

    try {
      await onConfirm(preflight.valid, {
        kind,
        comment: "",
        rejection_reason: isApproval ? "" : trimmedReason,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex max-h-[70vh] flex-col space-y-4 overflow-hidden">
      <div
        className={`flex items-center gap-2 ${
          isApproval ? "text-green-600" : "text-amber-600"
        }`}
      >
        {isApproval ? (
          <Check className="h-5 w-5" />
        ) : (
          <AlertTriangle className="h-5 w-5" />
        )}
        <span className="font-medium">
          {PAYROLL_VOUCHER_LABELS.BATCH_CONFIRM_TITLE(actionLabel)}
        </span>
      </div>

      <p className="text-sm text-gray-600">
        {PAYROLL_VOUCHER_LABELS.BATCH_INTRO(actionLabel, count)}
      </p>

      <p
        className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm font-medium text-[#613F4F]"
        data-testid={BATCH_EFFECT_TEST_ID}
      >
        {isApproval
          ? PAYROLL_VOUCHER_LABELS.BATCH_APPROVE_EFFECT(count)
          : PAYROLL_VOUCHER_LABELS.BATCH_REJECT_EFFECT(count)}
      </p>

      <div
        className="max-h-[40vh] flex-1 space-y-2 overflow-y-auto pr-2"
        data-testid={BATCH_PREFLIGHT_TEST_ID}
      >
        <span className="text-xs font-medium uppercase text-gray-500">
          {`${PAYROLL_VOUCHER_LABELS.BATCH_WILL_PASS} (${count})`}
        </span>

        {count === 0 && (
          <p className="text-sm text-amber-700">
            {PAYROLL_VOUCHER_LABELS.BATCH_NOTHING_TO_PROCESS}
          </p>
        )}

        {preflight.valid.map((voucher: PayrollVoucher) => (
          <div
            key={voucher.id}
            className="rounded-lg border bg-gray-50 p-3 text-sm"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <div className="font-medium text-[#613F4F]">
                  {voucher.employee_name}
                </div>
                <div className="space-y-0.5 text-xs text-gray-500">
                  <div>{`Loja ${voucher.store_code}`}</div>
                  <div>{formatDateToDMY(voucher.conciliation_date)}</div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="rounded border border-gray-200 bg-white px-2 py-0.5 text-xs font-medium text-gray-800">
                  {`${installmentsOf(voucher)}x`}
                </span>
                <span className="text-xs text-gray-500">
                  {formatToBRL(
                    Math.abs(
                      installmentAmountsOf(voucher)[FIRST_INSTALLMENT_INDEX] ?? 0,
                    ),
                  )}
                </span>
                <span className="font-semibold text-gray-900">
                  {formatToBRL(Math.abs(voucher.amount))}
                </span>
              </div>
            </div>
          </div>
        ))}

        {preflight.skipped.length > 0 && (
          <div className="space-y-2 pt-2" data-testid={BATCH_SKIPPED_TEST_ID}>
            <span className="text-xs font-medium uppercase text-gray-500">
              {`${PAYROLL_VOUCHER_LABELS.BATCH_WILL_SKIP} (${preflight.skipped.length})`}
            </span>

            {preflight.skipped.map((skipped: VoucherSkip) => (
              <div
                key={skipped.voucher.id}
                className="rounded-lg border border-dashed bg-white p-3 text-sm opacity-70"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium text-gray-600">
                    {skipped.voucher.employee_name}
                  </span>
                  <span className="text-xs text-amber-700">
                    {skipLabelOf(skipped.reason)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {!isApproval && (
        <div className="space-y-1">
          <label
            htmlFor={BATCH_REASON_FIELD_ID}
            className="text-sm font-medium text-gray-700"
          >
            {PAYROLL_VOUCHER_LABELS.REJECTION_REASON_LABEL}
          </label>
          <Textarea
            id={BATCH_REASON_FIELD_ID}
            data-testid={BATCH_REASON_TEST_ID}
            value={reason}
            onChange={event => setReason(event.target.value)}
            placeholder={PAYROLL_VOUCHER_LABELS.REJECTION_REASON_PLACEHOLDER}
            maxLength={MAX_COMMENT_LENGTH}
            disabled={isSubmitting}
            aria-invalid={showReasonError && !reasonIsValid}
            rows={3}
            className="min-h-[80px] text-sm"
          />
          <p
            className={`text-xs ${
              showReasonError && !reasonIsValid
                ? "text-red-600"
                : "text-gray-500"
            }`}
          >
            {showReasonError && !reasonIsValid
              ? PAYROLL_VOUCHER_LABELS.REJECTION_REASON_TOO_SHORT(
                  DECISION_REASON_MIN_LENGTH,
                )
              : PAYROLL_VOUCHER_LABELS.REJECTION_REASON_HINT(
                  DECISION_REASON_MIN_LENGTH,
                  MAX_COMMENT_LENGTH,
                )}
          </p>
        </div>
      )}

      <div className="flex items-center justify-between border-t pt-4">
        <span className="text-sm text-gray-500">
          {`${PAYROLL_VOUCHER_LABELS.BATCH_TOTAL}: ${count}`}
        </span>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            {PAYROLL_VOUCHER_LABELS.CANCEL}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isSubmitting || count === 0}
            className={
              isApproval
                ? "bg-green-600 text-white hover:bg-green-700"
                : "bg-[#F6D040] text-[#613F4F] hover:bg-[#F6D040]/90"
            }
          >
            {isSubmitting ? (
              PAYROLL_VOUCHER_LABELS.PROCESSING
            ) : (
              <>
                {isApproval ? (
                  <Check className="mr-2 h-4 w-4" />
                ) : (
                  <X className="mr-2 h-4 w-4" />
                )}
                {`${actionLabel} (${count})`}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
